import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRole } from "@/contexts/RoleContext";
import { prestatairesRepository } from "@/repositories/prestatairesRepository";
import type { PrestataireRow, PrestataireUpdate, ContactRow } from "@/schemas/prestataire";

export const prestatairesKeys = {
  all: ["prestataires"] as const,
  list: (role: string) => ["prestataires", "list", role] as const,
  detail: (role: string, id: string) => ["prestataires", "detail", role, id] as const,
  contacts: (role: string, prestataireId: string) =>
    ["prestataires", "contacts", role, prestataireId] as const,
};

export function usePrestataires() {
  const { role } = useRole();
  return useQuery<PrestataireRow[]>({
    queryKey: prestatairesKeys.list(role),
    queryFn: () => prestatairesRepository.list(role),
  });
}

export function usePrestataire(id: string | undefined) {
  const { role } = useRole();
  return useQuery<PrestataireRow | null>({
    queryKey: id ? prestatairesKeys.detail(role, id) : ["prestataires", "detail", role, "__none__"],
    queryFn: () => (id ? prestatairesRepository.get(role, id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function usePrestataireContacts(prestataireId: string | undefined) {
  const { role } = useRole();
  return useQuery<ContactRow[]>({
    queryKey: prestataireId
      ? prestatairesKeys.contacts(role, prestataireId)
      : ["prestataires", "contacts", role, "__none__"],
    queryFn: () =>
      prestataireId ? prestatairesRepository.getContacts(role, prestataireId) : Promise.resolve([]),
    enabled: !!prestataireId,
  });
}

export function useUpdatePrestataire(id: string) {
  const { role } = useRole();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PrestataireUpdate) => prestatairesRepository.update(role, id, patch),
    onSuccess: (updated) => {
      qc.setQueryData(prestatairesKeys.detail(role, id), updated);
      qc.invalidateQueries({ queryKey: prestatairesKeys.all });
    },
  });
}
